import React, { useCallback, useMemo, useState } from 'react'
import {
  CheckBoxFieldv2,
  DatePickerV2,
  labraUIDateFormat,
  LabraToolTipV2,
  NestedDropdown,
  TableComponentV2,
  TextInputV4,
} from '@labrav/react-components'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import { useStore } from 'react-redux'
import {
  useDispatch,
  useSelector,
  type RootState,
} from '../../../../../../store'
import { useStyles } from './MarketplaceTabFieldConfiguration.styles'
import {
  MarketplaceSalesforceFieldsData,
  MarketplaceTemplateDataResponse,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'
import {
  setMarketplaceReferralTemplate,
  updateMarketplaceTemplateSectionField,
} from '../../../../../../modules/marketplaceMappingManager/action'
import { MarketplaceMappingManagerFileUpload } from './MarketplaceMappingManagerFileUpload'
import {
  buildSalesforceCrmNestedMenu,
  sortCrmPicklistOptions,
} from '../../../../utils/mappingManagerConstants'

type PicklistOption = { label: string; value: string }
type SalesforceFieldMapping = {
  label?: string
  referralField?: string
  referralFieldType?: string
  crmField?: string
  crmFieldLabel?: string
  isHidden?: boolean
  isReadonly?: boolean
  isMapped?: boolean
  defaultValue?: string | string[] | boolean | null
  picklist?: PicklistOption[]
  displayOrder?: number
}
type RowState = SalesforceFieldMapping & {
  key: keyof MarketplaceTemplatesDataFieldMappings | string
}

const tableMapping = {
  'Fields to map': 'fieldsToMap',
  'Field type': 'fieldType',
  'Salesforce fields': 'salesforceFields',
  'Default value': 'defaultValue',
  Hidden: 'hidden',
  'Read only': 'readOnly',
}

const ACCOUNT_ID_REFERRAL_FIELD = 'Labra__Account_Id__c'

const SECTION_ROW_BY_REFERRAL_FIELD: Record<
  string,
  { sectionLabel: string; referralField: string }
> = {
  Labra__Metadata_Hide__c: {
    sectionLabel: 'Metadata',
    referralField: 'Labra__Metadata_Hide__c',
  },
  Labra__Stakeholders__c: {
    sectionLabel: 'Stakeholders',
    referralField: 'Labra__Stakeholders__c',
  },
}

function getReferralFieldFromRow(row: Record<string, unknown>): string {
  return String(
    row.referralField ??
      (row as { referral_field?: string }).referral_field ??
      ''
  )
}

function getReferralFieldFromMappingValue(v: unknown): string {
  if (!v || typeof v !== 'object') return ''
  const o = v as Record<string, unknown>
  return String(o.referralField ?? o.referral_field ?? '')
}

function isBottomSectionMappingRow(row: Record<string, unknown>): boolean {
  return getReferralFieldFromRow(row) in SECTION_ROW_BY_REFERRAL_FIELD
}

function findMappingKeyByReferralField(
  fieldsMapping: Record<string, unknown>,
  referralField: string
): string | undefined {
  for (const [k, v] of Object.entries(fieldsMapping)) {
    if (getReferralFieldFromMappingValue(v) === referralField) return k
  }
  return undefined
}

/** Salesforce templates nest rows under `fieldMappings.fields.fieldsMapping`; avoid changing shared reducer behavior for HubSpot. */
export function mergeNestedSalesforceFieldsMappingPatch(
  current: MarketplaceTemplateDataResponse | undefined,
  fieldKey: string,
  updates: Record<string, unknown>
): MarketplaceTemplateDataResponse | null {
  if (!current?.fieldMappings) return null
  const fm = current.fieldMappings as Record<string, unknown>
  const fieldsBucket = fm.fields as Record<string, unknown> | undefined
  const mappingProp =
    fieldsBucket && fieldsBucket.fieldsMapping != null ? 'fieldsMapping' : null

  if (!fieldsBucket || !mappingProp) return null
  const inner = fieldsBucket[mappingProp] as Record<string, unknown> | undefined
  if (!inner || typeof inner !== 'object') return null

  const prev = (inner[fieldKey] as Record<string, unknown>) ?? {}
  return {
    ...current,
    fieldMappings: {
      ...fm,
      fields: {
        ...fieldsBucket,
        [mappingProp]: {
          ...inner,
          [fieldKey]: { ...prev, ...updates },
        },
      },
    } as MarketplaceTemplateDataResponse['fieldMappings'],
  }
}

function fieldMappingTooltip(
  row: Record<string, unknown>,
  fallback: string
): string {
  const help = String(row.helpText ?? row.help_text ?? '').trim()
  if (help) return help
  const desc = String(row.description ?? '').trim()
  if (desc) return desc
  return fallback
}

function getCrmFieldValue(row: SalesforceFieldMapping): string {
  return String(row.crmField ?? '')
}

function getCrmFieldLabelValue(row: SalesforceFieldMapping): string {
  return String(row.crmFieldLabel ?? '')
}

export function getDefaultValueValue(
  row: SalesforceFieldMapping
): string | string[] | boolean | null {
  return row.defaultValue ?? null
}

export function hasMappedCrmField(row: SalesforceFieldMapping): boolean {
  return !!getCrmFieldValue(row)
}

export function hasDefaultValue(row: SalesforceFieldMapping): boolean {
  const value = getDefaultValueValue(row)
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return value
  return value != null && String(value).trim() !== ''
}

/** True after `updates` if there is a CRM mapping or a non-empty default (whitespace-only text is empty). */
function computeFieldIsMapped(
  row: SalesforceFieldMapping,
  updates: Partial<SalesforceFieldMapping>
): boolean {
  const merged: SalesforceFieldMapping = { ...row, ...updates }
  return hasMappedCrmField(merged) || hasDefaultValue(merged)
}

function MarketplaceMappingDefaultDatePicker(props: {
  fieldKey: string
  value: string | string[]
  handleDateChange: (date: string | null) => void
  disabled: boolean
  isViewOnly?: boolean
}) {
  const { fieldKey, value, handleDateChange, disabled, isViewOnly } = props
  const { classes } = useStyles()
  return (
    <div className={classes.inputText}>
      <DatePickerV2
        format={labraUIDateFormat}
        views={['year', 'month', 'day']}
        field={{
          title: '',
          humanReadableKey: 'Select an option',
          placeHolder: 'Select an option',
          id: `marketplace-defaultValue-${fieldKey}`,
          isRequired: false,
          isReadOnly: disabled || isViewOnly,
          isCustom: false,
          defaultValue: value,
          dataType: 'date',
          minDate: 'today',
        }}
        handleDateChange={handleDateChange}
        size="small"
      />
    </div>
  )
}

export const MarketplaceTabFieldConfiguration = React.memo(({
  searchTerm = '',
  fieldFilter = 'all',
}: {
  searchTerm?: string
  fieldFilter?: 'all' | 'mapped' | 'unmapped'
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const reduxStore = useStore<RootState>()
  const location = useLocation()
  const isViewOnly =
    new URLSearchParams(location.search).get('viewOnly') === 'true'

  const bottomSectionFieldKeys = useSelector(state => {
    const templateData: any =
      state.marketplaceMappingManager?.templateData?.templateData
    const fm: Record<string, unknown> =
      templateData?.fieldMappings?.fields?.fieldsMapping || {}

    const metaKey = findMappingKeyByReferralField(fm, 'Labra__Metadata_Hide__c')
    const stakeKey = findMappingKeyByReferralField(fm, 'Labra__Stakeholders__c')

    const metaVal = metaKey
      ? (fm[metaKey] as Record<string, unknown>)
      : undefined
    const stakeVal = stakeKey
      ? (fm[stakeKey] as Record<string, unknown>)
      : undefined

    return {
      metadataKey: metaKey,
      stakeholdersKey: stakeKey,
      metadataHidden: !!(
        metaVal?.isHidden ?? (metaVal as { is_hidden?: boolean })?.is_hidden
      ),
      stakeholdersHidden: !!(
        stakeVal?.isHidden ?? (stakeVal as { is_hidden?: boolean })?.is_hidden
      ),
    }
  })

  const fieldsMapping = useSelector<Record<string, SalesforceFieldMapping>>(
    state => {
      const templateData: any =
        state.marketplaceMappingManager?.templateData?.templateData
      return templateData?.fieldMappings?.fields?.fieldsMapping || {}
    }
  )

  const salesforceFieldsData = useSelector<MarketplaceSalesforceFieldsData[]>(
    state => state.marketplaceMappingManager.salesforceFields.fields || []
  )

  const parseCrmField = (crmField?: string) => {
    if (!crmField || typeof crmField !== 'string') return null
    const parts = crmField.split('.')
    if (parts.length < 2) return null
    return { objectType: parts[0], fieldApi: parts.slice(1).join('.') }
  }

  const crmMenu = useMemo(
    () => buildSalesforceCrmNestedMenu(salesforceFieldsData),
    [salesforceFieldsData]
  )

  const CustomHeaderCell = useCallback(
    ({ label, className }: { label: string; className?: string }) => (
      <TableCell className={className || classes.tableCell}>
        <Typography className={classes.header}>{label}</Typography>
      </TableCell>
    ),
    [classes.tableCell, classes.header]
  )

  const initialRows = useMemo<RowState[]>(
    () =>
      Object.entries(fieldsMapping)
        .map(([key, value]) => ({ key, ...value }) as RowState)
        .filter(row => !isBottomSectionMappingRow(row))
        .sort((a, b) => {
          const orderA =
            a.displayOrder ??
            (a as { display_order?: number }).display_order ??
            Number.MAX_SAFE_INTEGER
          const orderB =
            b.displayOrder ??
            (b as { display_order?: number }).display_order ??
            Number.MAX_SAFE_INTEGER
          return orderA - orderB
        }),
    [fieldsMapping]
  )

  const [rows, setRows] = useState<RowState[]>(initialRows)
  React.useEffect(() => setRows(initialRows), [initialRows])

  const visibleRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    return rows.filter(row => {
      const matchesSearch =
        !query || String(row.label || row.key).toLowerCase().includes(query)
      const isMapped = hasMappedCrmField(row) || hasDefaultValue(row)
      return (
        matchesSearch &&
        (fieldFilter === 'all' ||
          (fieldFilter === 'mapped' ? isMapped : !isMapped))
      )
    })
  }, [rows, searchTerm, fieldFilter])

  const dispatchFieldMappingUpdate = useCallback(
    (fieldKey: string, updates: Partial<RowState>) => {
      const current = reduxStore.getState().marketplaceMappingManager
        .templateData.templateData as
        | MarketplaceTemplateDataResponse
        | undefined
      const merged = mergeNestedSalesforceFieldsMappingPatch(
        current,
        fieldKey,
        updates as Record<string, unknown>
      )
      if (merged) {
        dispatch(setMarketplaceReferralTemplate(merged))
        return
      }
      dispatch(
        updateMarketplaceTemplateSectionField(
          fieldKey as keyof MarketplaceTemplatesDataFieldMappings,
          updates as any
        )
      )
    },
    [dispatch, reduxStore]
  )

  const updateRow = (key: string, updates: Partial<RowState>) => {
    setRows(prev =>
      prev.map(row => (row.key === key ? { ...row, ...updates } : row))
    )
    dispatchFieldMappingUpdate(key, updates)
  }

  const updateSectionHidden = useCallback(
    (mappingKey: string | undefined, isHidden: boolean) => {
      if (!mappingKey) return
      dispatchFieldMappingUpdate(mappingKey, { isHidden })
    },
    [dispatchFieldMappingUpdate]
  )

  const objects = useMemo(
    () =>
      visibleRows.map(row => {
        const isPicklist = row.referralFieldType === 'Picklist'
        const isMultiPicklist = row.referralFieldType === 'Multipicklist'
        const isDate = row.referralFieldType === 'Date'
        const isFile = row.referralFieldType === '.txt/.pdf'
        const rowDefaultValue = getDefaultValueValue(row)
        const defaultValue = Array.isArray(rowDefaultValue)
          ? rowDefaultValue[0] || ''
          : rowDefaultValue == null
            ? ''
            : String(rowDefaultValue)
        const rowHasCrmField = hasMappedCrmField(row)
        const rowHasDefaultValue = hasDefaultValue(row)

        const referralFieldType = row.referralFieldType || ''
        const crmParsed = parseCrmField(getCrmFieldValue(row))
        const crmFieldLabelValue = getCrmFieldLabelValue(row)
        const defaultSelectedItems =
          crmParsed && crmFieldLabelValue
            ? [
                {
                  value: crmParsed.fieldApi,
                  path: `${crmParsed.objectType}.${crmFieldLabelValue}`,
                },
              ]
            : []

        const menuDataForRow = crmMenu

        const referralFieldApi = getReferralFieldFromRow(
          row as unknown as Record<string, unknown>
        )
        const isAccountIdRow = referralFieldApi === ACCOUNT_ID_REFERRAL_FIELD
        const accountIdTooltip = isAccountIdRow

        return {
          id: row.referralField || row.key,
          fieldsToMap: (
            <div className={classes.fieldsToMapRow}>
              {isAccountIdRow ? (
                <div className={classes.fieldLabelWithInfo}>
                  <Typography className={classes.fieldsToMapLabelWithInfo}>
                    {row.label || row.key}
                  </Typography>
                  <LabraToolTipV2
                    tooltipPlacement="top-start"
                    description={
                      accountIdTooltip ||
                      'Identifies the subscriber account for this marketplace referral.'
                    }
                    tooltipPlacmentGap="12px"
                  >
                    {/* 
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className={classes.fieldInfoIcon}
                      color="#1942C5"
                    /> */}
                  </LabraToolTipV2>
                </div>
              ) : (
                <Typography className={classes.fieldsToMap}>
                  {row.label || row.key}
                </Typography>
              )}
            </div>
          ),
          fieldType: (
            <div className={classes.fieldsToMapRow}>
              <Typography className={classes.fieldsToMap}>
                {isMultiPicklist
                  ? 'Multi Picklist'
                  : isFile
                    ? 'File'
                    : row.referralFieldType || '-'}
              </Typography>
            </div>
          ),
          salesforceFields: (
            <div className={classes.inputSelect}>
              <NestedDropdown
                menuData={menuDataForRow}
                placeholder="Select an option"
                width="267px"
                height="44px"
                allowMultiple={false}
                defaultSelectedItems={defaultSelectedItems}
                onSelectionChange={(selectedValues, selectedPaths) => {
                  const selectedValue = selectedValues[0] || ''
                  const selectedPath = selectedPaths[0] || ''
                  const pathParts = selectedPath.split('.')
                  const crmObject = pathParts[0] || ''
                  const crmFieldLabelOnly = selectedValue
                    ? pathParts.slice(1).join('.') || selectedPath
                    : ''

                  const crmFieldVal =
                    crmObject && selectedValue
                      ? `${crmObject}.${selectedValue}`
                      : selectedValue
                  const updates: Partial<SalesforceFieldMapping> = {
                    crmField: crmFieldVal,
                    crmFieldLabel: crmFieldLabelOnly,
                    ...(selectedValue
                      ? { defaultValue: '', default_value: '' }
                      : {}),
                  }
                  updateRow(row.key, {
                    ...updates,
                    isMapped: computeFieldIsMapped(row, updates),
                  })
                }}
                disabled={isViewOnly || rowHasDefaultValue}
              />
            </div>
          ),
          defaultValue:
            isPicklist || isMultiPicklist ? (
              <div className={classes.inputSelect}>
                <NestedDropdown
                  menuData={[
                    { label: '--None--', value: '', options: [] },
                    ...sortCrmPicklistOptions(row.picklist || []).map(
                      option => ({
                        ...option,
                        options: [],
                      })
                    ),
                  ]}
                  placeholder="Select an option"
                  width="267px"
                  height="44px"
                  allowMultiple={isMultiPicklist}
                  defaultSelectedItems={
                    Array.isArray(rowDefaultValue)
                      ? rowDefaultValue.map(v => ({ value: v, path: v }))
                      : rowDefaultValue
                        ? [
                            {
                              value: String(rowDefaultValue),
                              path: `${row.picklist?.find(option => option.value === rowDefaultValue)?.label || ''}`,
                            },
                          ]
                        : []
                  }
                  onSelectionChange={selectedValues => {
                    const updates: Partial<SalesforceFieldMapping> = {
                      defaultValue: isMultiPicklist
                        ? selectedValues
                        : selectedValues[0] || '',
                      ...(selectedValues.length
                        ? {
                            crmField: '',
                            crmFieldLabel: '',
                          }
                        : {}),
                    }
                    updateRow(row.key, {
                      ...updates,
                      isMapped: computeFieldIsMapped(row, updates),
                    })
                  }}
                  disabled={isViewOnly || rowHasCrmField}
                />
              </div>
            ) : isDate ? (
              <MarketplaceMappingDefaultDatePicker
                fieldKey={`${row.key}`}
                value={
                  Array.isArray(row.defaultValue)
                    ? row.defaultValue[0] || ''
                    : (row.defaultValue as string) || ''
                }
                handleDateChange={date => {
                  const updates: Partial<SalesforceFieldMapping> = {
                    defaultValue: date ? date : '',
                    ...(date
                      ? {
                          crmField: '',
                          crmFieldLabel: '',
                        }
                      : {}),
                  }
                  updateRow(row.key, {
                    ...updates,
                    isMapped: computeFieldIsMapped(row, updates),
                  })
                }}
                disabled={rowHasCrmField}
                isViewOnly={isViewOnly}
              />
            ) : isFile ? (
              <MarketplaceMappingManagerFileUpload
                fieldKey={
                  row.key as keyof MarketplaceTemplatesDataFieldMappings
                }
                disabled={isViewOnly || rowHasCrmField}
              />
            ) : (
              <div className={classes.inputText}>
                <TextInputV4
                  keyName={`${row.key}-default-value`}
                  title=""
                  placeHolder=""
                  type={row.referralFieldType === 'Number' ? 'number' : 'text'}
                  value={defaultValue}
                  updatedValue={defaultValue}
                  isError={false}
                  onChange={e => {
                    const v = e.target.value
                    const updates: Partial<SalesforceFieldMapping> = {
                      defaultValue: v,
                      ...(v
                        ? {
                            crmField: '',
                            crmFieldLabel: '',
                          }
                        : {}),
                    }
                    updateRow(row.key, {
                      ...updates,
                      isMapped: computeFieldIsMapped(row, updates),
                    })
                  }}
                  isReadOnly={rowHasCrmField}
                  isRequired={false}
                  errorText=""
                  showSpinner={false}
                  viewOnly={isViewOnly || rowHasCrmField}
                />
              </div>
            ),
          hidden: (
            <div className={classes.checkBox}>
              <CheckBoxFieldv2
                keyName={`${row.key}-hidden`}
                title=""
                value={!!row.isHidden}
                onChangeBox={e =>
                  updateRow(row.key, { isHidden: e.target.checked })
                }
                disabled={!!row.isReadonly || isViewOnly}
              />
            </div>
          ),
          readOnly: (
            <div className={classes.checkBox}>
              <CheckBoxFieldv2
                keyName={`${row.key}-readonly`}
                title=""
                value={!!row.isReadonly}
                onChangeBox={e =>
                  updateRow(row.key, { isReadonly: e.target.checked })
                }
                disabled={!!row.isHidden || isViewOnly}
              />
            </div>
          ),
        }
      }),
    [visibleRows, classes, crmMenu, isViewOnly]
  )

  const customHeader = useMemo(
    () => ({
      fieldsToMap: CustomHeaderCell({
        label: 'Fields to map',
        className: clsx(classes.tableCell, classes.fieldsToMapCell),
      }),
      fieldType: CustomHeaderCell({
        label: 'Field type',
        className: clsx(classes.tableCell, classes.fieldTypeCell),
      }),
      salesforceFields: CustomHeaderCell({
        label: 'Salesforce fields',
        className: clsx(classes.tableCell, classes.crmFieldsCell),
      }),
      defaultValue: CustomHeaderCell({
        label: 'Default value',
        className: clsx(classes.tableCell, classes.defaultValueCell),
      }),
      hidden: CustomHeaderCell({
        label: 'Hidden',
        className: clsx(classes.tableCell),
      }),
      readOnly: CustomHeaderCell({
        label: 'Read only',
        className: clsx(classes.tableCell),
      }),
    }),
    [CustomHeaderCell, classes]
  )

  const sectionVisibilityRows = useMemo(
    () =>
      [
        {
          id: 'Labra__Metadata_Hide__c' as const,
          sectionLabel:
            SECTION_ROW_BY_REFERRAL_FIELD.Labra__Metadata_Hide__c.sectionLabel,
          mappingKey: bottomSectionFieldKeys.metadataKey,
          hidden: bottomSectionFieldKeys.metadataHidden,
        },
        {
          id: 'Labra__Stakeholders__c' as const,
          sectionLabel:
            SECTION_ROW_BY_REFERRAL_FIELD.Labra__Stakeholders__c.sectionLabel,
          mappingKey: bottomSectionFieldKeys.stakeholdersKey,
          hidden: bottomSectionFieldKeys.stakeholdersHidden,
        },
      ] as const,
    [
      bottomSectionFieldKeys.metadataHidden,
      bottomSectionFieldKeys.metadataKey,
      bottomSectionFieldKeys.stakeholdersHidden,
      bottomSectionFieldKeys.stakeholdersKey,
    ]
  )

  if (Object.keys(fieldsMapping).length === 0) {
    return (
      <div className={classes.tableContainer}>
        <Typography className={classes.fieldsToMap}>
          No fields found for this template.
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.tableContainer} data-testid="salesforce-fields-tab">
      {objects.length > 0 ? (
        <TableComponentV2
          keyName="marketplace-salesforce-fields-table"
          objects={objects}
          customHeader={customHeader}
          mapping={tableMapping}
          fetchMoreData={() => {}}
          selectedItem={[]}
          onChangeCheckbox={() => {}}
          showCheckbox={false}
          tableHeight="calc(100%)"
          breakWorkHeader={false}
          hasMoreData={false}
        />
      ) : null}

      <Paper
        elevation={1}
        className={classes.sectionsPaper}
        data-testid="marketplace-section-visibility"
      >
        <TableContainer className={classes.sectionsTableScroll}>
          <Table className={classes.sectionsTable} padding="normal">
            <TableHead>
              <TableRow className={classes.sectionTableHeadRow}>
                <TableCell
                  className={clsx(classes.tableCell, classes.sectionColumnCell)}
                >
                  <Typography component="div" className={classes.header}>
                    Section
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.sectionHiddenColumnCell
                  )}
                  align="center"
                >
                  <Typography component="div" className={classes.header}>
                    Hidden
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.tableCell,
                    classes.sectionRowFillerHead
                  )}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {sectionVisibilityRows.map(row => (
                <TableRow key={row.id} className={classes.sectionStripedRow}>
                  <TableCell
                    className={clsx(
                      classes.sectionTableBodyCell,
                      classes.sectionColumnCell
                    )}
                  >
                    <div className={classes.fieldsToMapRow}>
                      <Typography
                        component="div"
                        className={classes.fieldsToMap}
                      >
                        {row.sectionLabel}
                      </Typography>
                    </div>
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.sectionTableBodyCell,
                      classes.sectionHiddenColumnCell
                    )}
                    align="center"
                  >
                    <div
                      className={clsx(
                        classes.checkBox,
                        classes.sectionCheckBox
                      )}
                    >
                      <CheckBoxFieldv2
                        keyName={`section-${row.id}-hidden`}
                        title=""
                        value={row.hidden}
                        onChangeBox={e =>
                          updateSectionHidden(row.mappingKey, e.target.checked)
                        }
                        disabled={isViewOnly || !row.mappingKey}
                      />
                    </div>
                  </TableCell>
                  <TableCell
                    className={clsx(
                      classes.sectionTableBodyCell,
                      classes.sectionRowFiller
                    )}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  )
})
